// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type {Ball} from './Ball'
import type {World} from './World'
import {applyContactImpulse,correctPenetration} from './contact'
import {PENETRATION_SLOP} from './constants'
import type {MaterialName} from './materials'

/** A finite cylindrical support with rounded dressed ends, in playfield coordinates. */
export interface CapsuleProfile {readonly a:readonly [number,number,number];readonly b:readonly [number,number,number];readonly radius:number}
export class Capsule {
 private readonly ax:number;private readonly ay:number;private readonly az:number;private readonly bx:number;private readonly by:number;private readonly bz:number;private readonly tube:number
 private readonly minX:number;private readonly maxX:number;private readonly minY:number;private readonly maxY:number;private readonly lo:number;private readonly hi:number
 private nx=0;private ny=0;private nz=1;private depth=0
 private readonly material:MaterialName='metal'
 impactSpeed=0
 constructor(p:CapsuleProfile){this.ax=p.a[0];this.ay=p.a[1];this.az=p.a[2];this.bx=p.b[0];this.by=p.b[1];this.bz=p.b[2];this.tube=p.radius;this.minX=Math.min(this.ax,this.bx)-p.radius;this.maxX=Math.max(this.ax,this.bx)+p.radius;this.minY=Math.min(this.ay,this.by)-p.radius;this.maxY=Math.max(this.ay,this.by)+p.radius;this.lo=Math.min(this.az,this.bz)-p.radius;this.hi=Math.max(this.az,this.bz)+p.radius}
 private probe(x:number,y:number,z:number,r:number):void{
  const dx=this.bx-this.ax,dy=this.by-this.ay,dz=this.bz-this.az,ss=dx*dx+dy*dy+dz*dz,t=ss>0?Math.max(0,Math.min(1,((x-this.ax)*dx+(y-this.ay)*dy+(z-this.az)*dz)/ss)):0,vx=x-this.ax-t*dx,vy=y-this.ay-t*dy,vz=z-this.az-t*dz,distance=Math.sqrt(vx*vx+vy*vy+vz*vz)
  this.nx=distance>0?vx/distance:0;this.ny=distance>0?vy/distance:0;this.nz=distance>0?vz/distance:1;this.depth=r+this.tube-distance
 }
  resolve(world:World,ball:Ball):boolean{
    if(ball.layer!==0)return false
    const x=ball.x-world.frameX,y=ball.y-world.frameY,z=ball.z,r=ball.radius,x0=ball.prevX-world.frameX,y0=ball.prevY-world.frameY,z0=ball.prevZ
    if(Math.max(x,x0)+r<this.minX||Math.min(x,x0)-r>this.maxX||Math.max(y,y0)+r<this.minY||Math.min(y,y0)-r>this.maxY||Math.max(z,z0)+r<this.lo||Math.min(z,z0)-r>this.hi)return false
    this.probe(x,y,z,r)
    if(this.depth<=PENETRATION_SLOP){
      const dx=x-x0,dy=y-y0,dz=z-z0,travel=Math.sqrt(dx*dx+dy*dy+dz*dz)
      if(travel<r*.5)return false
      this.probe(x0,y0,z0,r);if(this.depth>=-PENETRATION_SLOP)return false
      let t=0,found=false
      for(let i=0;i<64;i++){
        this.probe(x0+dx*t,y0+dy*t,z0+dz*t,r)
        if(this.depth>=-1e-8){found=true;break}
        t+=-this.depth/travel*.95;if(t>1)break
      }
      if(!found)return false
      ball.x=x0+dx*t+world.frameX;ball.y=y0+dy*t+world.frameY;ball.z=z0+dz*t
    }
    correctPenetration(ball,this.nx,this.ny,this.nz,Math.max(0,this.depth))
    this.impactSpeed=applyContactImpulse(ball,this.nx,this.ny,this.nz,world.materials[this.material],world.frameVX,world.frameVY,0,world.rng).impactSpeed
    return true
  }
}
interface SupportNode {minX:number;maxX:number;minY:number;maxY:number;lo:number;hi:number;start:number;end:number;left:SupportNode|undefined;right:SupportNode|undefined}
/** Ordered hierarchy keeps contact work local; no mesh or per-step allocation is needed. */
export class CapsuleChain {
 private readonly capsules:Capsule[];private readonly root:SupportNode|undefined
 constructor(profiles:readonly CapsuleProfile[]){
  this.capsules=profiles.map(p=>new Capsule(p))
  const build=(start:number,end:number):SupportNode=>{
   const node:SupportNode={minX:Infinity,maxX:-Infinity,minY:Infinity,maxY:-Infinity,lo:Infinity,hi:-Infinity,start,end,left:undefined,right:undefined}
   for(let i=start;i<end;i++){const p=profiles[i]!;node.minX=Math.min(node.minX,p.a[0]-p.radius,p.b[0]-p.radius);node.maxX=Math.max(node.maxX,p.a[0]+p.radius,p.b[0]+p.radius);node.minY=Math.min(node.minY,p.a[1]-p.radius,p.b[1]-p.radius);node.maxY=Math.max(node.maxY,p.a[1]+p.radius,p.b[1]+p.radius);node.lo=Math.min(node.lo,p.a[2]-p.radius,p.b[2]-p.radius);node.hi=Math.max(node.hi,p.a[2]+p.radius,p.b[2]+p.radius)}
   if(end-start>8){const mid=(start+end)>>>1;node.left=build(start,mid);node.right=build(mid,end)}return node
  }
  this.root=profiles.length?build(0,profiles.length):undefined
 }
 resolve(world:World,ball:Ball):boolean{return this.root!==undefined&&ball.heldBy<0&&ball.layer===0&&this.visit(this.root,world,ball)}
 private visit(n:SupportNode,w:World,b:Ball):boolean{
  const r=b.radius,x=b.x-w.frameX,y=b.y-w.frameY,x0=b.prevX-w.frameX,y0=b.prevY-w.frameY
  if(Math.max(x,x0)+r<n.minX||Math.min(x,x0)-r>n.maxX||Math.max(y,y0)+r<n.minY||Math.min(y,y0)-r>n.maxY||Math.max(b.z,b.prevZ)+r<n.lo||Math.min(b.z,b.prevZ)-r>n.hi)return false
  let hit=false;if(n.left&&n.right){hit=this.visit(n.left,w,b);return this.visit(n.right,w,b)||hit}
  for(let i=n.start;i<n.end;i++)if(this.capsules[i]!.resolve(w,b))hit=true
  return hit
 }
}
